import React, { ReactNode, ChangeEvent } from 'react'
import clsx from 'clsx'
import { Radio as RadioStyles } from '@airbus/elements/elements'
import uniqueId from 'lodash.uniqueid'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useControlled from '../../lib/hooks/useControlled'
import { CommonProps } from '../helperTypes'

type Props = {
  /** If `true`, the component is checked */
  checked?: boolean
  /** checked icon component */
  checkedIcon?: ReactNode
  /** The default checked status for an uncontrolled component */
  defaultChecked?: boolean
  /** unchecked icon component */
  icon?: ReactNode
  /**
   * The id that will be passed to the underlying input element.
   */
  id?: string
  /** The label to show next to the `<input />` */
  label?: string
  /** The callback for when the input checked status changes */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  /**
   * The size of the checkbox
   * @default medium
   */
  size?: 'medium' | 'large'
}

type DeprecatedProps = {
  large?: boolean
}

export type RadioProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: RadioProps) => {
  const { large, ...rest } = props
  return {
    ...(large && { size: 'large' }),
    ...rest,
  }
}

const useStyles = createUseStyles(RadioStyles, 'radio')

const Radio = (props: RadioProps) => {
  const {
    id: propsId,
    checked: checkedProp,
    defaultChecked,
    onChange,
    label,
    size,
    icon,
    checkedIcon,
    className,
    style,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Radio', { large: 'size' }, props)
  const defaultId = React.useRef<string>(uniqueId('Radio-'))
  const inputEl = React.useRef(null)
  const classes = useStyles()
  const [checked, setCheckedState] = useControlled({
    value: checkedProp,
    defaultValue: defaultChecked,
  })

  const id = propsId || defaultId.current
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckedState(e.target.checked)
    onChange && onChange(e)
  }
  return (
    <div className={clsx(className, classes.root, size && classes[`_${size}`], icon && classes._icon)} style={style}>
      <input
        className={classes.input}
        ref={inputEl}
        id={id}
        type='radio'
        checked={checked}
        onChange={handleChange}
        {...rest}
      />
      {icon && checkedIcon && (checked ? checkedIcon : icon)}
      {
        // if an icon is not provided, we need a label, even empty, for the :before/:after magic of the default radio
        (label || !icon) && (
          <label className={classes.label} htmlFor={id}>
            {label}
          </label>
        )
      }
    </div>
  )
}

export default Radio
